import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ProgettoCard from "@/components/ProgettoCard";
import ProgettoLightbox from "@/components/ProgettoLightbox";
import { listProgetti } from "@/lib/api";
import { projectCategories } from "@/lib/project";

export default function Progetti() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("Tutti");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    listProgetti()
      .then(setProjects)
      .catch((err) => setError(err.message || "Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const all = new Set();
    projects.forEach((p) => projectCategories(p).forEach((c) => all.add(c)));
    return ["Tutti", ...Array.from(all)];
  }, [projects]);

  const filteredProjects = useMemo(
    () =>
      activeCategory === "Tutti"
        ? projects
        : projects.filter((p) => projectCategories(p).includes(activeCategory)),
    [projects, activeCategory]
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navigation
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <main className="pt-28 md:pt-32">
        {/* Header */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-12 md:pb-16">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] bg-clip-text text-transparent bg-gradient-to-r from-orange-500 via-blue-500 to-purple-500 mb-4">
              Portfolio
            </p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0B1E36] mb-6">
              I Nostri Progetti
            </h1>
            <p className="text-base md:text-lg text-[#64748B] leading-relaxed">
              Tutte le installazioni realizzate da Finestre e Colore. Ogni
              progetto racconta la nostra dedizione alla qualità, al design e
              alla soddisfazione del cliente.
            </p>
          </div>
        </section>

        {/* Filters */}
        {!loading && projects.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-10">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((cat) => {
                const isActive = cat === activeCategory;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all border ${
                      isActive
                        ? "bg-[#0B1E36] text-white border-[#0B1E36] shadow-md"
                        : "bg-white text-[#0B1E36] border-[#E2E8F0] hover:border-[#0B1E36]"
                    }`}
                    data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-24 md:pb-32">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#0B1E36]" />
            </div>
          ) : error ? (
            <p className="text-center text-red-600">Errore: {error}</p>
          ) : projects.length === 0 ? (
            <p className="text-center text-[#64748B]">
              I nostri progetti saranno presto disponibili. Torna a trovarci!
            </p>
          ) : filteredProjects.length === 0 ? (
            <p className="text-center text-[#64748B]">
              Nessun progetto in questa categoria.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project, index) => (
                <ProgettoCard
                  key={project.id ?? index}
                  project={project}
                  onClick={() => setSelected(project)}
                  testId={`project-item-${index}`}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
      <WhatsAppButton />

      <ProgettoLightbox project={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
